import { useEffect, useMemo, useState } from "react";
import { CarouselWoList } from "../models/ResponseModels/CarouselResponse";
import ErrorsString from "../ErrorsString";
import Spinner from "./Spinner";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  EFFECT_COVERFLOW_SWIPER,
  INITIAL_SLIDE,
  SLIDES_PER_VIEW,
} from "../Utilities";
import {
  EffectCoverflow,
  Pagination,
  Autoplay,
  Navigation,
} from "swiper/modules";
import Alert from "@mui/material/Alert";
import config from "../config";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import TestLogin from "../models/TestLogin";
import { GetCarouselPublicWoModel } from "../models/GetCarouselPublicWoModel";
import { useNavigate } from "react-router-dom";
import CustomPagination from "./CustomPagination";

interface Props {
  idPage: string;
}

export default function CarouselHome(props: Props) {
  let [carouselWo, setCarouselWo] = useState<CarouselWoList[]>([]);
  let [alertMessage, setAlertMessage] = useState<string>("");
  let [showAlert, setShowAlert] = useState<boolean>(false);
  const [spinnerVisibility, setSpinnerVisibility] = useState(false);
  const navigate = useNavigate();
  let [isLogged, setIsLogged] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      const isLoggedIn = await TestLogin();
      //isLoggedIn = true;
      setIsLogged(isLoggedIn);
      getCarouselWo(isLoggedIn);
    })();
  }, []);

  const safeSlides = useMemo(() => {
    if (carouselWo.length > SLIDES_PER_VIEW) {
      return carouselWo;
    } else {
      return [...carouselWo, ...carouselWo];
    }
  }, [carouselWo]);

  //console.log(safeSlides);

  function getCarouselWo(isLoggedIn: boolean) {
    window.scrollTo(0, 0);
    setSpinnerVisibility(true);

    const params = new URLSearchParams(window.location.search);
    const tokenFromUrl = params.get("token") || null;

    (async () => {
      let response = null;
      response = await GetCarouselPublicWoModel(props.idPage, tokenFromUrl);

      //console.log("GetCarouselPublicWoModel response: " + response);
      const responseData = response?.data;
      const statusCode = response?.status;

      if (responseData === undefined) {
        setShowAlert(true);
        setAlertMessage(ErrorsString.errorMessageGeneric);
        setSpinnerVisibility(false);
        return;
      }

      if (statusCode !== 200) {
        setShowAlert(true);
        setAlertMessage(ErrorsString.errorMessageGeneric);
        setSpinnerVisibility(false);
        return;
      }

      if (responseData.code !== "0") {
        setShowAlert(true);
        setAlertMessage(responseData.message);
        setSpinnerVisibility(false);
        return;
      }

      if (responseData.carouselWoList.length > 0) {
        setCarouselWo(responseData.carouselWoList);
        setShowAlert(false);
        setAlertMessage("");
        setSpinnerVisibility(false);
      } else {
        setShowAlert(true);
        setAlertMessage(ErrorsString.errorMessageCarousel);
        setSpinnerVisibility(false);
      }
    })();
  }

  function openWorkOrderPage(uuid: string, imageUrl: string) {
    if (isLogged) {
      const parameters = {
        previewImageUrl: imageUrl,
      };
      navigate("/details/" + uuid, { state: JSON.stringify(parameters) });
    }
  }
  return (
    <>
      <Spinner visibility={spinnerVisibility} />
      <div className="container mb-4">
        <div className="relative w-full h-[450px] overflow-hidden">
          {safeSlides.length > 0 ? (
            <Swiper
              spaceBetween={0}
              slidesPerView={3}
              initialSlide={INITIAL_SLIDE}
              navigation={true}
              pagination={false}
              loop={true}
              centeredSlides={true}
              autoplay={{
                delay: 10000,
                disableOnInteraction: false,
              }}
              effect="coverflow"
              coverflowEffect={EFFECT_COVERFLOW_SWIPER}
              modules={[Pagination, EffectCoverflow, Autoplay, Navigation]}
              breakpoints={{
                768: {
                  slidesPerView: SLIDES_PER_VIEW,
                },
              }}
              className="absolute top-0 left-1/2 bottom-0 inset-x-2/4 -translate-x-1/2 max-w-[1920px]  md:w-[1920px]"
              style={{
                position: "absolute",
              }}
            >
              {safeSlides.map((item) => (
                <SwiperSlide key={item.workorderId}>
                  {({ isActive, isVisible }) => (
                    <div
                      className={`h-full w-[180%] ml-[-35%] ${
                        isVisible ? "flex" : "hidden"
                      } items-center`}
                    >
                      <div
                        className="relative w-full  aspect-w-16 aspect-h-9 h-[370px] text-white"
                        onClick={() =>
                          openWorkOrderPage(
                            item.workorderId,
                            item.images[0].imageUrl
                          )
                        }
                      >
                        <img
                          src={
                            item.images?.length > 0
                              ? config.cloudfrontImgUrl +
                                "" +
                                item.images[0].imageUrl +
                                config.showImagesQuery
                              : config.placeholderImageUrl
                          }
                          className="absolute top-0 left-0 h-full w-full rounded-md object-cover"
                          style={{
                            filter: !isActive
                              ? "grayscale(75%)"
                              : "grayscale(0%)",
                            transition: "filter 500ms",
                          }}
                          alt={item.title}
                        />

                        <div
                          className="absolute bottom-0 left-0 right-0 p-6 rounded-md"
                          style={{
                            background:
                              "linear-gradient(0deg, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 80%, rgba(0,0,0,0.0) 100%)",
                          }}
                        >
                          <h3 className="m-0 font-skyBold text-lg">
                            {item.title}
                          </h3>
                          {item.description ? (
                            <p className="m-0">
                              {item.description.slice(0, 160)}...
                            </p>
                          ) : (
                            <></>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </SwiperSlide>
              ))}

              <CustomPagination data={carouselWo} />
            </Swiper>
          ) : (
            <Alert
              severity="error"
              style={{
                display: showAlert ? "flex" : "none",
              }}
            >
              {alertMessage}
            </Alert>
          )}
        </div>
      </div>
    </>
  );
}
